/**
 * Cérebro do bot (item 5.1): roda a árvore da posição a cada decisão (o intervalo depende do nível) e, a cada
 * tick, transforma a intenção em comando: direção até o alvo, subida, turbo, botões tocados e a carga do
 * arremesso. Tudo determinístico: o mesmo estado dá o mesmo comando, no cliente e no servidor.
 */
import { BOT_LEVEL_PARAMS, FOULS, hoopCenters, type BotLevel } from "@besenliga/shared";
import {
  BUTTON,
  DEFENDS,
  NO_INPUT,
  quantizeInput,
  type AthleteState,
  type InputCommand,
  type Vec3,
  type WorldState,
} from "@besenliga/sim";
import { dist, opponents, type Ctx, type Intent } from "./context.js";
import { HOOP_BLOCK_SAFE, TREES } from "./trees.js";

/** Hash inteiro simples (FNV-1a) do id, para o "acaso" de cada bot. */
function idHash(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Valor em [-1, 1) a partir de inteiros, sem estado (xorshift de uma rodada). */
function mix(a: number, b: number, k: number): number {
  let x = (a ^ Math.imul(b + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(k + 1, 0xc2b2ae35)) >>> 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  return ((x >>> 0) / 4294967296) * 2 - 1;
}

export class BotBrain {
  readonly hash: number;
  private intent: Intent = { target: null, arrive: true, turbo: false, tap: 0, charge: 0 };
  /** Erro de mira desta decisão (m). */
  private err: Vec3 = { x: 0, y: 0, z: 0 };
  private chargeLeft = 0;
  private charging = false;
  private nearOwnHoops = 0;
  /** Nome das ações escolhidas na última decisão (para depuração). */
  lastTrace: string[] = [];

  constructor(
    readonly id: string,
    readonly level: BotLevel,
  ) {
    this.hash = idHash(id);
  }

  /** Comando deste bot para o tick que vem depois de `w`. */
  command(w: Readonly<WorldState>): InputCommand {
    const me = w.athletes.find((a) => a.id === this.id);
    if (!me || !w.balls) return NO_INPUT;
    const p = BOT_LEVEL_PARAMS[this.level];
    // Cada bot decide num tick diferente (pelo hash), para o time não "piscar" junto.
    if ((w.tick + (this.hash % p.thinkTicks)) % p.thinkTicks === 0) this.think(w, me);

    const it = this.intent;
    let buttons = 0;
    // Toques valem um tick; depois somem (a simulação reage à borda do botão).
    if (it.tap) {
      buttons |= it.tap;
      it.tap = 0;
    }
    // Carga do arremesso: segura o botão por `charge` ticks e solta (a soltura arremessa).
    const holding = w.balls.pelota.holder === me.id;
    if (it.charge > 0 && holding && !this.charging) {
      this.charging = true;
      this.chargeLeft = it.charge;
      it.charge = 0;
    }
    if (this.charging) {
      if (!holding || this.chargeLeft <= 0) this.charging = false;
      else {
        buttons |= BUTTON.charge;
        this.chargeLeft -= 1;
      }
    }

    if (!it.target) return { ...NO_INPUT, buttons };
    const t = { x: it.target.x + this.err.x, y: it.target.y + this.err.y, z: it.target.z + this.err.z };
    let dx = t.x - me.position.x;
    let dz = t.z - me.position.z;
    const h = Math.sqrt(dx * dx + dz * dz);
    let scale = h > 0.3 ? 1 / h : 0;
    if (it.arrive) scale *= Math.min(1, h / 8);
    dx *= scale;
    dz *= scale;
    const climb = Math.max(-1, Math.min(1, (t.y - me.position.y) / 4));
    let turbo = it.turbo && h > 14 && me.stamina > p.turboStamina && !me.exhausted;

    if (p.avoidsFouls) {
      // Não atropelar: com um adversário logo à frente, tira o turbo e alivia.
      const speed = Math.sqrt(me.velocity.x * me.velocity.x + me.velocity.z * me.velocity.z);
      const ahead = opponents({ w, me } as Ctx).some((o) => {
        const d = dist(o.position, me.position);
        if (d > 5) return false;
        const fx = (o.position.x - me.position.x) / (d || 1);
        const fz = (o.position.z - me.position.z) / (d || 1);
        return (
          fx * me.velocity.x + fz * me.velocity.z > FOULS.pushSpeed * 0.7 ||
          (turbo && fx * me.heading.x + fz * me.heading.z > 0.5)
        );
      });
      if (ahead) {
        turbo = false;
        if (speed > FOULS.pushSpeed * 0.75) {
          dx *= 0.3;
          dz *= 0.3;
        }
      }
    }
    return quantizeInput(dx, climb, dz, buttons | (turbo ? BUTTON.turbo : 0));
  }

  private think(w: Readonly<WorldState>, me: AthleteState): void {
    const p = BOT_LEVEL_PARAMS[this.level];
    const c: Ctx = {
      w,
      me,
      level: this.level,
      p,
      intent: { target: null, arrive: true, turbo: false, tap: this.intent.tap, charge: 0 },
      trace: [],
      noise: (k) => mix(this.hash, w.tick, k),
    };
    TREES[me.role].tick(c);

    if (p.avoidsFouls && me.role !== "goleiro") this.avoidHoopBlock(c, me, p.thinkTicks);
    this.intent = c.intent;
    this.lastTrace = c.trace;
    this.err = { x: c.noise(1) * p.aimError, y: c.noise(2) * p.aimError * 0.3, z: c.noise(3) * p.aimError };
  }

  /** Quem não é goleiro e está perto dos próprios aros há um tempo sai dali antes de virar falta. */
  private avoidHoopBlock(c: Ctx, me: AthleteState, every: number): void {
    const side = DEFENDS[me.team];
    const near = hoopCenters(side).some((h) => dist(h, me.position) < HOOP_BLOCK_SAFE);
    this.nearOwnHoops = near ? this.nearOwnHoops + every : 0;
    if (this.nearOwnHoops > FOULS.hoopBlockSeconds * 30 * 0.5) {
      c.intent.target = { x: side * 30, y: 14, z: me.position.z };
      c.intent.arrive = false;
      c.trace.push("sai da frente do aro");
    }
  }
}

/** Todos os bots de uma partida: um cérebro por atleta controlado. */
export class BotTeam {
  private readonly brains: BotBrain[];

  constructor(entries: readonly { id: string; level: BotLevel }[]) {
    this.brains = entries.map((e) => new BotBrain(e.id, e.level));
  }

  /** Comandos de todos os bots para o próximo tick. */
  inputs(w: Readonly<WorldState>): Record<string, InputCommand> {
    const out: Record<string, InputCommand> = {};
    for (const b of this.brains) out[b.id] = b.command(w);
    return out;
  }

  brain(id: string): BotBrain | undefined {
    return this.brains.find((b) => b.id === id);
  }
}
